import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import { ILoadType } from "@/interfaces/loadtype.interface";
import { IVehicle } from "@/interfaces/vehicle.interface";
import { IWasteType } from "@/interfaces/wastetype.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

type wasteManageType = "load-type" | "waste-type" | "vehicle";
export const wasteManagementTypeApi = createApi({
  reducerPath: "wasteManagementTypeQuery",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "vehicle",
    "waste-type",
    "load-type",
    "single-waste-type",
    "single-load-type",
    "single-vehicle",
  ],
  endpoints: (builder) => ({
    //get single waste type
    getSingleWasteType: builder.query<IWasteType, { id: string }>({
      query: ({ id }) =>
        `workspace/${getWorkspace()}/waste-management/waste-type/${id}`,
      providesTags: ["single-waste-type"],
      transformResponse: (response: GeneralResponse<IWasteType>) =>
        response?.data,
    }),

    //get single load type
    getSingleLoadType: builder.query<ILoadType, { id: string }>({
      query: ({ id }) =>
        `workspace/${getWorkspace()}/waste-management/load-type/${id}`,
      providesTags: ["single-load-type"],
      transformResponse: (response: GeneralResponse<ILoadType>) =>
        response?.data,
    }),

    //get single vehicle
    getSingleVehicle: builder.query<IVehicle, { id: string }>({
      query: ({ id }) =>
        `workspace/${getWorkspace()}/waste-management/vehicle/${id}`,
      providesTags: ["single-vehicle"],
      transformResponse: (response: GeneralResponse<IVehicle>) =>
        response?.data,
    }),

    //General queries for delete and update

    // delete data
    deleteTypeData: builder.mutation<
      object,
      { id: string; type: wasteManageType }
    >({
      query: ({ id, type }) => ({
        url: `workspace/${getWorkspace()}/waste-management/${type}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { type }) => {
        return [type];
      },
    }),

    // update data
    updateTypeData: builder.mutation<
      object,
      { id: string; type: wasteManageType; data: object }
    >({
      query: ({ id, type, data }) => ({
        url: `workspace/${getWorkspace()}/waste-management/${type}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { type }) => {
        return [`single-${type}`];
      },
    }),

    // LOAD TYPE

    //get the list of load type
    getLoadType: builder.query<
      GeneralResponse<ILoadType[]>,
      { filter?: string }
    >({
      query: ({ filter }) =>
        `workspace/${getWorkspace()}/waste-management/load-type${filter || ""}`,
      providesTags: ["load-type"],
    }),

    //add load type
    addLoadType: builder.mutation<object, unknown>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/waste-management/load-type`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: ["load-type"],
    }),

    // WASTE TYPE

    //get the list of waste type
    getWasteType: builder.query<
      GeneralResponse<IWasteType[]>,
      { filter?: string }
    >({
      query: ({ filter }) =>
        `workspace/${getWorkspace()}/waste-management/waste-type${filter || ""}`,
      providesTags: ["waste-type"],
    }),

    //add waste type
    addWasteType: builder.mutation<object, { data: object; type: string }>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/waste-management/waste-type`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: ["waste-type"],
    }),

    //VEHICLES

    //get the list of vehicle
    getVehicles: builder.query<
      GeneralResponse<IVehicle[]>,
      { filter?: string }
    >({
      query: ({ filter }) =>
        `workspace/${getWorkspace()}/waste-management/vehicle${filter || ""}`,
      providesTags: ["vehicle"],
    }),

    //add vehicle
    addVehicle: builder.mutation<object, unknown>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/waste-management/vehicle`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: ["vehicle"],
    }),
  }),
});

export const {
  useGetLoadTypeQuery,
  useGetWasteTypeQuery,
  useGetVehiclesQuery,
  useGetSingleLoadTypeQuery,
  useGetSingleWasteTypeQuery,
  useGetSingleVehicleQuery,
  useAddLoadTypeMutation,
  useAddVehicleMutation,
  useAddWasteTypeMutation,
  useDeleteTypeDataMutation,
  useUpdateTypeDataMutation,
} = wasteManagementTypeApi;
